<template>
  <div>
    <div>
      <v-container class="pa-0 pl-3">
        <v-row class="pa-0 pl-3">
          <v-radio-group
            :disabled="hasEssentials"
            v-model="numDays"
            @change="handleChangeDays"
            row
          >
            <v-radio
              v-for="day in possibleDays"
              :key="day.value"
              :label="day.name"
              :value="day.value"
            ></v-radio>
          </v-radio-group>
        </v-row>
        <v-row class="pa-0 pl-3">
          <v-col class="pa-0 ma-0" cols="4">
            <div class="dropdown-script application-heading">Sort</div>
            <v-select
              :disabled="hasEssentials"
              v-model="sortOption"
              :items="sortOptions"
              item-value="value"
              item-text="name"
              @change="sortChange"
              class="mt-0 pt-0"
            ></v-select>
          </v-col>
          <v-col class="pa-0 pl-6 ma-0" cols="3">
            <div class="dropdown-script application-heading">Order</div>
            <div>
              <a
                @click="orderClick"
                class="d-flex"
                :class="{ disabled: hasEssentials }"
              >
                <v-img
                  class="pa-1 sort-icon"
                  src="@/assets/svgs/sort.svg"
                ></v-img>
                <div class="mt-2 ml-1">{{ this.sortDirection }}</div>
              </a>
            </div>
          </v-col>
          <v-col class="pa-0 pr-1 ma-0" cols="5">
            <div class="dropdown-script application-heading">Filter</div>
            <v-select
              :disabled="hasEssentials"
              v-model="selectedFilters"
              @change="handleFilterChange"
              :items="filterOptions"
              item-text="name"
              item-value="value"
              multiple
              chips
              small-chips
              deletable-chips
              dense
            />
          </v-col>
        </v-row>
      </v-container>
    </div>
    <Loader v-if="loading" text="" color="black" />
    <v-container
      scrollable
      class="hyp-scroll"
      v-if="pulses && !hasEssentials"
      @scroll="handleScroll"
    >
      <div class="GaugeHolderSvg">
        <div v-for="pulse in pulses" :key="pulse.country">
          <div v-show="pulse.visible">
            <a @click="openWatch(pulse.country)" class="countryLine">
              <span class="application-heading">{{ pulse.countryName }} </span>
            </a>
          </div>
          <div v-show="pulse.visible" class="stability-holder">
            <a @click="openWatch(pulse.country)"
              ><v-img :src="pulse.flagUrl" class="flag"></v-img
            ></a>
            <StabilityGaugeSvg
              :reference="pulse.country"
              :dark="false"
              :gauge-type="'horizontal'"
              :pulse-score-current="pulse.count"
              :pulse-score-delta="pulse.scoreDelta"
              :pulse-average="pulse.average"
              class="ml-4"
            ></StabilityGaugeSvg>
          </div>
        </div>
      </div>
    </v-container>
    <v-container v-if="hasEssentials">
      <div class="GaugeHolderSvg">
        <div>
          <v-img src="@/assets/images/SamplePulseDelta.png"></v-img>
        </div>
        <div class="essentials-message mt-3">
          <p>
            Would you like to receive early warnings of deteriorating stability
            levels around the globe? Pulse AI is our stability model, alerting
            you to important developments and improving your speed to action.
          </p>
          <p>
            Contact your Seerist account manager or
            <a href="mailto:support@seerist.com">support@seerist.com</a> to
            learn more.
          </p>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import { MODULE_PREFIX_HOME, MODULE_PREFIX_USER } from "@/store";
import { SAVE_PREFERENCES } from "@/store/actions.type";
import { GET_DETAILS_SIDEBAR } from "@/store/actions.type";
import { hasEssentialsAccess } from "@/services/user/user.service";
import { getStabilityDelta } from "@/services/grids/stability.service";
import { getAllCountries, getAllRegions } from "@/common/maps/utilities";
import { formatFlagUrl } from "@/common/basic.utilities";
import { GRIDS_STABILITY_TYPES } from "@/common/constants/grids";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";

import { mapGetters } from "vuex";
import lodashGet from "lodash/get";
import lodashOrderBy from "lodash/orderBy";
import lodashDebounce from "lodash/debounce";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

export default {
  name: "PulseChanges",
  components: {
    StabilityGaugeSvg: () =>
      import("@/components/grids/stability/StabilityGaugeSvg"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    params: {
      type: Object,
    },
    openedTimestamp: {
      type: String,
    },
    analyticsCategory: {
      type: String,
      default: EVENT_CATEGORIES.pulse_changes,
    },
    analyticsCategoryNumDays: {
      type: String,
      default: EVENT_CATEGORIES.pulse_changes_num_days,
    },
    analyticsCategorySort: {
      type: String,
      default: EVENT_CATEGORIES.pulse_changes_sort,
    },
    analyticsCategoryFilter: {
      type: String,
      default: EVENT_CATEGORIES.pulse_changes_filter,
    },
    analyticsCategoryCountryLink: {
      type: String,
      default: EVENT_CATEGORIES.pulse_changes_item,
    },
  },
  data() {
    return {
      loading: false,
      hasEssentials: hasEssentialsAccess(),
      numDays: 1,
      possibleDays: [
        { name: "24 Hours", value: 1 },
        { name: "7 Days", value: 7 },
        { name: "30 Days", value: 30 },
      ],
      sortOption: "scoreDelta",
      sortOptions: [
        { name: "Change in Stability", value: "scoreDelta" },
        { name: "Current Stability Score", value: "count" },
        { name: "Alphabetical Order", value: "countryName" },
      ],
      selectedFilters: [],
      filterOptions: [{ name: "Outside Average", value: "outlier" }],
      sortDirection: "Asc",
      sortImage: "fa-solid fa-arrow-up-arrow-down",
      pulses: [],
      allPulses: [],
      countries: [],
      combinedRegionCountryFilters: [],
      regions: [],
      lastScroll: 0,
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
    }),
    crRisksFeatureUnlocked() {
      return this.featureUnlocked("cr_risks");
    },
  },
  methods: {
    formatData(pulseObj) {
      try {
        let country = this.getCountryByCode(pulseObj.country);
        pulseObj.countryName = country.data.properties.displayName;
        pulseObj.count = Math.floor(pulseObj.count);
        pulseObj.scoreDelta = Math.floor(pulseObj.scoreDelta);
        pulseObj.flagUrl = formatFlagUrl(country.data.properties.iso3);
        if (pulseObj.stats && pulseObj.stats.percentiles)
          pulseObj.average = `${Math.floor(
            pulseObj.stats.percentiles.very_low
          )} - ${Math.floor(pulseObj.stats.percentiles.very_high)}`;
        else pulseObj.average = "";
        pulseObj.visible = true;
      } catch (e) {
        pulseObj.visible = false;
        console.error(
          "Malformed pulse delta or country data. Country code: ",
          pulseObj?.countryName,
          e
        );
      }
    },
    sortChange() {
      this.handlePageTrackerEvent(
        this.analyticsCategorySort,
        EVENT_ACTIONS.changed,
        `${this.sortOption} ${this.sortDirection}`
      );

      this.sortContent();
      this.setPrefs();
    },
    handleChangeDays() {
      this.handlePageTrackerEvent(
        this.analyticsCategoryNumDays,
        EVENT_ACTIONS.changed,
        `${this.numDays} days`
      );

      this.getContent();
      this.setPrefs();
    },
    orderClick() {
      if (this.sortDirection === "Asc") {
        this.sortDirection = "Desc";
      } else {
        this.sortDirection = "Asc";
      }
      this.sortContent();
      this.setPrefs();
    },
    openWatch(code) {
      let type = GRIDS_STABILITY_TYPES.country;

      this.handlePageTrackerEvent(
        this.analyticsCategoryCountryLink,
        EVENT_ACTIONS.clicked,
        `Country link - ${type}`
      );

      this.detailsSidebar.visible = true;
      this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.monitor;
      this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.monitor;
      this.detailsSidebar.monitorType = DETAIL_SIDEBAR_TYPES.stability;
      this.detailsSidebar.stabilityType = type;
      this.detailsSidebar.code = code;
      this.detailsSidebar.openMonitor = true;

      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );
    },
    getParams() {
      let paramsCopy = {};
      paramsCopy.locationType = "country"; // currently only support country
      paramsCopy.numDays = this.numDays;
      return paramsCopy;
    },
    getContent() {
      this.loading = true;
      this.pulses = [];
      this.allPulses = [];
      this.getContentDebounced();
    },
    getContentDebounced: lodashDebounce(async function () {
      let result;
      let dataParams = {};
      try {
        this.loading = true;
        dataParams = this.getParams();
        this.setPrefs();

        result = await getStabilityDelta(dataParams);
        if (result && result.pulseTiles) {
          let pulseTiles = result.pulseTiles;
          pulseTiles.forEach((p) => this.formatData(p));
          this.allPulses = pulseTiles;
          this.pulses = pulseTiles;
          this.filterContent();
          this.sortContent();
        } else {
          this.pulses = null;
        }
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
        console.error(error);
      } finally {
        this.loading = false;
        result = null;
      }
    }, 100),
    filterContent() {
      // TODO - handle multiple potential filters
      if (this.selectedFilters && this.selectedFilters.length > 0) {
        this.pulses = this.pulses.filter(
          (p) =>
            p.count < p.stats.percentiles.very_low ||
            p.count > p.stats.percentiles.very_high
        );
      }
      // apply country/region filters
      if (
        this.combinedRegionCountryFilters &&
        this.combinedRegionCountryFilters.length > 0
      ) {
        this.pulses = this.pulses.filter(
          (p) =>
            this.combinedRegionCountryFilters.indexOf(
              p.country.toUpperCase()
            ) >= 0
        );
      }
    },
    sortContent() {
      this.pulses = lodashOrderBy(
        this.pulses,
        this.sortOption,
        this.sortDirection.toLowerCase()
      );
    },
    getCountryByCode(code) {
      let res = this.countries.find((obj) => {
        return (
          (obj.properties.iso2 &&
            obj.properties.iso2.toUpperCase() === code.toUpperCase()) ||
          (obj.properties.iso3 &&
            obj.properties.iso3.toUpperCase() === code.toUpperCase()) ||
          (obj.properties.code &&
            obj.properties.code.toUpperCase() === code.toUpperCase())
        );
      });
      res = {
        data: res,
      };
      return res;
    },
    applyPrefs() {
      let latestPulsePrefs =
        lodashGet(this.preferences, "latest.pulseDelta") || {};
      this.selectedFilters = latestPulsePrefs.selectedFilters || [];
      this.sortDirection = latestPulsePrefs.sortDirection || "Asc";
      this.sortOption = latestPulsePrefs.sortOption || "scoreDelta";
      this.numDays = latestPulsePrefs.numDays || 1;
    },
    setPrefs() {
      let latestPulsePrefs = {
        selectedFilters: this.selectedFilters,
        sortDirection: this.sortDirection,
        sortOption: this.sortOption,
        numDays: this.numDays,
      };
      if (this.preferences) {
        if (!this.preferences.latest) {
          this.preferences.latest = {};
        }
        this.preferences.latest.pulseDelta = latestPulsePrefs;
        this.$store.dispatch(
          MODULE_PREFIX_USER + SAVE_PREFERENCES,
          this.preferences
        );
      }
    },
    /**
     * Gets the list of countries in each region filter and concatinates with the country filters to
     * create one combined country code filter list. This is because the Pulse delta API only filters
     * on countries, currently.
     */
    combineRegionCountryFilters() {
      //If all regions are selected, we don't need to send an aois filter to the Pulse Delta API
      if (this.params.allRegionsSelected) {
        this.combinedRegionCountryFilters = [];
        return;
      }

      let newCombinedFilters = [];

      //Get the list of countries in each region
      if (this.params.regionFilters && this.params.regionFilters.length) {
        this.params.regionFilters.forEach((regionFilter) => {
          let region = this.regions.find((r) => {
            return r.value === regionFilter;
          });
          if (region) {
            region.countries?.forEach((c) => {
              newCombinedFilters.push(c.value);
            });
          }
        });
      }

      if (this.params.countryFilters) {
        newCombinedFilters = newCombinedFilters.concat(
          this.params.countryFilters
        );
      }

      //Remove duplicate countries by casting as a Set
      if (newCombinedFilters.length > 0) {
        newCombinedFilters = [...new Set(newCombinedFilters)];
      }
      this.combinedRegionCountryFilters = newCombinedFilters;
    },
    handleFilterChange() {
      if (this.selectedFilters?.length && this.analyticsCategoryFilter) {
        this.handlePageTrackerEvent(
          this.analyticsCategoryFilter,
          EVENT_ACTIONS.changed,
          `${this.selectedFilters}`
        );
      } else if (this.analyticsCategoryFilter) {
        this.handlePageTrackerEvent(
          this.analyticsCategoryFilter,
          EVENT_ACTIONS.clear,
          ""
        );
      }
    },
    handleScroll() {
      //Only track a new scroll event every minute so we don't send dozens of concurrent requests
      let isNewScrollEvent = new Date().valueOf() - this.lastScroll > 60000;

      if (isNewScrollEvent && this.analyticsCategory) {
        this.handlePageTrackerEvent(
          this.analyticsCategory,
          EVENT_ACTIONS.scrolled,
          ""
        );
        this.lastScroll = new Date().valueOf();
      }
    },
  },
  watch: {
    selectedFilters: lodashDebounce(function () {
      // reset the pulses back to all pulses then filter/sort
      this.pulses = this.allPulses;
      this.filterContent();
      this.sortContent();
      this.setPrefs();
    }, 500),
    params: {
      async handler() {
        this.combineRegionCountryFilters();
        // reset the pulses back to all pulses then filter/sort
        this.pulses = this.allPulses;
        this.filterContent();
        this.sortContent();
      },
      deep: true,
    },
    openedTimestamp: lodashDebounce(function () {
      this.getContent();
    }, 500),
  },
  mounted: async function () {
    if (!this.hasEssentials) {
      this.countries = await getAllCountries();
      this.regions =
        (await getAllRegions())?.map((r) => ({
          value: r.properties.code,
          text: r.properties.name,
          countries: r.properties.countries.map((c) => ({
            value: c.isoAlpha2Code,
            text: c.name,
          })),
        })) || [];

      this.combineRegionCountryFilters();
      this.applyPrefs();
      this.getContent();
    }
  },
};
</script>

<style scoped>
>>> .v-select__selection {
  font-size: 13px;
  font-weight: normal;
}
.GaugeHolderSvg {
  display: block;
  position: relative;
  height: 80px;
  min-width: 200px;
}

.dropdown-script {
  font-size: 12px;
  margin-bottom: 0;
}

.sort-icon {
  margin-top: 14px;
  max-width: 12px;
  width: 12px;
  height: 12px;
}

.disabled {
  pointer-events: none;
  cursor: default;
  text-decoration: none;
  color: rgba(0, 0, 0, 0.38);
}

.disabled .sort-icon {
  opacity: 0.38;
}

.stability-holder {
  display: flex;
}

.flag {
  height: 35px;
  max-height: 35px;
  width: 35px;
  max-width: 35px;
  border-radius: 18px;
  margin-right: -8px;
  margin-top: 10px;
}

.scrollable {
  height: calc(100vh - 255px);
  overflow-y: auto;
}
</style>
